/**
 * „Ich habe gekauft" — aus einem Zonensignal wird eine echte Position.
 *
 * DAS PROBLEM, das dieses Modul löst (Audit 10.08.2026): Die App wusste bisher
 * nur, was sie VORGESCHLAGEN hat, und was daraus geworden WÄRE. Sie wusste
 * nicht, was der Nutzer davon tatsächlich getan hat. `v5_zone_signals` kennt
 * nur den Lebenszyklus des Signals (offen / ausgestoppt / zeitausstieg), und
 * `broker_account` wurde im ganzen Projekt an keiner Stelle ausgewertet.
 *
 * Damit ließ sich genau eine Frage beantworten — „wie gut sind die Signale?" —
 * und die andere nicht: „wie gut folge ich ihnen?". Bei diesem Nutzer ist die
 * zweite die teurere: Alle vier realen Trades liefen gegen die
 * Zustandsmaschine (docs/konzepte/2026-08-08-die-ersten-vier-trades.md).
 *
 * ZWEI SPUREN, sauber getrennt:
 *   - BEOBACHTUNG: `v5_zone_signals`, allein vom Server-Cron aus den Bars
 *     aufgelöst. Läuft unabhängig davon weiter, ob der Nutzer den Trade
 *     übernommen hat — sonst wäre die Signalstatistik von seinem Verhalten
 *     abhängig und damit wertlos.
 *   - AUSFÜHRUNG: `v5_positions` mit `broker_account: 'revolut'`. Das ist,
 *     was er wirklich hält. Die Paper-Engine fasst diese Positionen nicht an
 *     (src/lib/paper-engine.ts, Filter auf 'paper').
 *
 * Kein Broker-Import. Der Nutzer trägt bewusst von Hand nach — er will die
 * Trades vor der Übernahme noch einmal ansehen.
 */

/** Was für die Stückzahl-Empfehlung gebraucht wird. */
export interface StueckzahlEingabe {
  /** Kaufpreis in Handelswährung. */
  einstieg: number;
  /** Stop in Handelswährung. Muss unter dem Einstieg liegen. */
  stop: number;
  /** Kontogröße in Kontowährung (z. B. 10.000 EUR). */
  kontogroesse: number;
  /** Risiko je Trade in Prozent (z. B. 0.5). */
  risikoProzent: number;
  /** Umrechnung Handels- → Kontowährung. */
  fxRate: number;
  /** Erlaubt der Broker Bruchstücke? Revolut: ja. */
  bruchstueckeErlaubt?: boolean;
}

export interface StueckzahlErgebnis {
  /** Empfohlene Stückzahl. 0, wenn nicht berechenbar. */
  stueck: number;
  /** Was diese Position bei Stop-Auslösung kostet, in Kontowährung. */
  risikoBetrag: number;
  /** Was die Position insgesamt bindet, in Kontowährung. */
  kapitalBindung: number;
  /** Warum es nicht ging — leer, wenn alles in Ordnung ist. */
  hinweis: string | null;
}

/**
 * Stückzahl aus dem Risiko, nicht aus dem Kapital.
 *
 * Der Abstand Einstieg → Stop IST das Risiko je Stück. Wie viele Stücke man
 * kauft, folgt daraus und aus dem Betrag, den man bereit ist zu verlieren —
 * nicht daraus, wie viel Geld gerade da ist. Das ist der einzige Punkt der
 * Positionsführung, den dieses Projekt nie in Frage gestellt hat, weil er
 * keine Prognose enthält, sondern eine Division.
 */
export function berechneStueckzahl(e: StueckzahlEingabe): StueckzahlErgebnis {
  const leer: StueckzahlErgebnis = {
    stueck: 0,
    risikoBetrag: 0,
    kapitalBindung: 0,
    hinweis: null,
  };

  if (!Number.isFinite(e.einstieg) || e.einstieg <= 0) {
    return { ...leer, hinweis: 'Kaufpreis fehlt.' };
  }
  if (!Number.isFinite(e.stop) || e.stop <= 0) {
    return { ...leer, hinweis: 'Stop fehlt.' };
  }
  if (e.stop >= e.einstieg) {
    // Kein Rechenfehler, sondern eine echte Warnung: Ein Stop über dem
    // Einstieg bedeutet, dass die Position bereits bei Eröffnung im Verlust
    // schliessen würde.
    return { ...leer, hinweis: 'Der Stop liegt nicht unter dem Kaufpreis.' };
  }
  if (!Number.isFinite(e.kontogroesse) || e.kontogroesse <= 0) {
    return { ...leer, hinweis: 'Kontogröße unbekannt.' };
  }
  if (!Number.isFinite(e.risikoProzent) || e.risikoProzent <= 0) {
    return { ...leer, hinweis: 'Risiko je Trade ist nicht gesetzt.' };
  }

  const fx = Number.isFinite(e.fxRate) && e.fxRate > 0 ? e.fxRate : 1;
  const risikoBudget = (e.kontogroesse * e.risikoProzent) / 100;
  const risikoJeStueckKonto = (e.einstieg - e.stop) * fx;
  const roh = risikoBudget / risikoJeStueckKonto;

  // Revolut erlaubt Bruchstücke. Wer ganze Stücke braucht, rundet ab —
  // aufrunden würde das vereinbarte Risiko überschreiten.
  const stueck = e.bruchstueckeErlaubt === false
    ? Math.floor(roh)
    : Math.floor(roh * 1000) / 1000;

  if (stueck <= 0) {
    return {
      ...leer,
      hinweis:
        'Bei diesem Stop-Abstand passt keine sinnvolle Stückzahl in das '
        + 'Risikobudget. Entweder ist der Stop zu weit oder das Konto zu klein.',
    };
  }

  const kapitalBindung = stueck * e.einstieg * fx;
  return {
    stueck,
    risikoBetrag: stueck * risikoJeStueckKonto,
    kapitalBindung,
    // Ehrlicher Hinweis statt stiller Annahme: Wenn die Position mehr bindet
    // als das Konto hergibt, ist die Rechnung zwar korrekt, aber unbrauchbar.
    hinweis:
      kapitalBindung > e.kontogroesse
        ? `Diese Position bände ${kapitalBindung.toFixed(0)} und damit mehr, `
          + `als das Konto trägt (${e.kontogroesse.toFixed(0)}). Der Stop ist `
          + `so eng, dass das Risikobudget erst bei einer zu großen Stückzahl `
          + `ausgeschöpft wäre.`
        : null,
  };
}
